import { useMutation, useQueryClient } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { InvokeConnectorFeatureResult } from '@/types/core/connector';
import type { UUID } from '@/utils/uuid';

import { connectorMutationKeys, connectorQueryKeys } from './query-keys';

const DUPLICATE_INVOCATION_MESSAGE =
  'This feature+sequence was already invoked';
const INVALID_SEQUENCE_MESSAGE = 'Invalid sequence or invocation in flight';

function mapInvokeFeatureError(error: unknown): Error {
  const message = error instanceof Error ? error.message : String(error);

  if (message.includes('INVOCATION_CONFLICT')) {
    return new Error(DUPLICATE_INVOCATION_MESSAGE);
  }

  if (message.includes('INVOCATION_PRECONDITION')) {
    return new Error(INVALID_SEQUENCE_MESSAGE);
  }

  return error instanceof Error
    ? error
    : new Error('Failed to run feature. Please try again.');
}

export function useInvokeConnectorFeature(tenantId?: UUID, connectorId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: connectorMutationKeys.requestConnectorInvocation(
      tenantId,
      connectorId
    ),
    mutationFn: async (payload: {
      featureId: UUID;
      runSequence?: number;
      options?: Record<string, unknown>;
      payload?: unknown;
    }): Promise<InvokeConnectorFeatureResult> => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!connectorId) throw new Error('Missing connector id');

      try {
        let runSequence = payload.runSequence;

        if (runSequence == null) {
          const currentSequence =
            await connectorService.getConnectorInvocationSequence(
              tenantId,
              connectorId,
              payload.featureId
            );

          // inFlight means a queued/requested run already owns this sequence —
          // re-request it rather than advancing to the next one.
          runSequence = currentSequence.inFlight
            ? currentSequence.sequenceNum
            : currentSequence.sequenceNum + 1;
        }

        return await connectorService.requestConnectorInvocation(
          tenantId,
          connectorId,
          payload.featureId,
          runSequence,
          payload.payload
        );
      } catch (error) {
        throw mapInvokeFeatureError(error);
      }
    },
    onSuccess: (data, variables) => {
      qc.invalidateQueries({
        queryKey: connectorQueryKeys.featureListByConnector(
          tenantId,
          connectorId
        ),
        refetchType: 'all',
      });

      qc.invalidateQueries({
        queryKey: connectorQueryKeys.currentInvocationStatus(
          tenantId,
          connectorId,
          variables.featureId
        ),
        refetchType: 'all',
      });

      qc.invalidateQueries({
        queryKey: connectorQueryKeys.invocationStatusesByFeature(
          tenantId,
          connectorId,
          variables.featureId
        ),
        refetchType: 'all',
      });

      const runSequence = data.runSequence
        ? parseInt(data.runSequence, 10)
        : undefined;
      if (runSequence != null && !isNaN(runSequence)) {
        qc.invalidateQueries({
          queryKey: connectorQueryKeys.invocationStatus(
            tenantId,
            connectorId,
            variables.featureId,
            runSequence
          ),
          refetchType: 'all',
        });
      }
    },
  });
}
